import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useDispatch } from "react-redux";

import { OAuth as OAuthActions } from "../../js/actions";
import { setSnackbar } from "../../js/reducers/snackbar";
import { setAlertDialog } from "../../js/reducers/alertDialog";

const NAME_REGEX = /^[a-zA-Z0-9_\s]+$/;

const ACCENT = "#2563eb";
const ACCENT_DARK = "#1d4ed8";
const TEXT_TITLE = "#0f172a";
const TEXT_PLACEHOLDER = "#94a3b8";
const FIELD_BG = "#f1f5f9";
const ERROR = "#dc2626";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
  },
  title: {
    fontFamily: theme.typography.fontFamily,
    background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`,
    color: "white",
    padding: theme.spacing(2, 2.5),
    "& h2": {
      fontFamily: theme.typography.fontFamily,
      fontWeight: theme.typography.fontWeightSemiBold,
      fontSize: "1rem !important",
      color: "white",
      paddingTop: 3,
    },
  },
  content: {
    padding: theme.spacing(2.5),
    paddingBottom: 0,
    fontFamily: theme.typography.fontFamily,
  },
  fieldGrid: {
    width: "100%",
    margin: 0,
    "& > .MuiGrid-item": {
      paddingTop: "0 !important",
      paddingBottom: "0 !important",
    },
    "& > .MuiGrid-item:nth-last-child(-n+2)": {
      marginBottom: 0,
    },
  },
  fieldGridItem: {
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "0 !important",
    paddingRight: "0 !important",
    marginBottom: theme.spacing(1.25),
  },
  fieldGridItemLeft: {
    paddingRight: `${theme.spacing(1)}px !important`,
  },
  fieldGridItemRight: {
    paddingLeft: `${theme.spacing(1)}px !important`,
  },
  fieldLabel: {
    color: TEXT_TITLE,
    fontFamily: theme.typography.fontFamily,
    fontSize: "0.85rem !important",
    fontWeight: theme.typography.fontWeightMedium,
    margin: 0,
    marginBottom: 2,
    padding: 0,
    display: "flex",
    alignItems: "center",
    gap: 4,
    lineHeight: 1.1,
  },
  required: {
    color: ERROR,
    fontWeight: theme.typography.fontWeightSemiBold,
  },
  field: {
    margin: "0 !important",
    "&.MuiFormControl-root": {
      marginTop: "0 !important",
    },
    "& .MuiFilledInput-root": {
      borderRadius: 10,
      backgroundColor: FIELD_BG,
      border: "1px solid transparent",
      fontFamily: theme.typography.fontFamily,
      padding: "0 !important",
      transition:
        "background-color 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
      "&:hover": {
        backgroundColor: "#e9eef5",
      },
      "&.Mui-focused": {
        backgroundColor: "white",
        borderColor: ACCENT,
        boxShadow: `0 0 0 3px rgba(37, 99, 235, 0.15)`,
      },
      "&.Mui-error": {
        borderColor: ERROR,
      },
      "&.Mui-error.Mui-focused": {
        boxShadow: `0 0 0 3px rgba(220, 38, 38, 0.12)`,
      },
      "&::before, &::after": {
        display: "none",
        content: "none",
        border: "none",
      },
    },
    "& .MuiFilledInput-input, & .MuiInputBase-input, & .MuiInputBase-inputHiddenLabel": {
      padding: "14px 12px !important",
      fontFamily: theme.typography.fontFamily,
      fontSize: "0.85rem !important",
      fontWeight: theme.typography.fontWeightRegular,
      color: TEXT_TITLE,
      "&::placeholder": {
        color: TEXT_PLACEHOLDER,
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeightRegular,
        opacity: 1,
      },
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 2,
      marginTop: 2,
      fontFamily: theme.typography.fontFamily,
      fontSize: "0.7rem !important",
      fontWeight: theme.typography.fontWeightRegular,
    },
  },
  selectMenuPaper: {
    marginTop: 4,
    borderRadius: 8,
    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.12)",
    maxHeight: 240,
    backgroundColor: "white",
    "& .MuiMenuItem-root": {
      fontFamily: theme.typography.fontFamily,
      fontSize: "0.82rem !important",
      minHeight: "auto",
      padding: theme.spacing(0.75, 1.5),
    },
    "& .MuiList-padding": {
      paddingTop: 4,
      paddingBottom: 4,
    },
  },
  actions: {
    padding: theme.spacing(1, 2.5, 1.5, 2.5),
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    "& > :not(:first-child)": {
      marginLeft: 0,
    },
  },
  primaryBtn: {
    textTransform: "none",
    backgroundColor: "#1d4ed8",
    color: "#fff",
    borderRadius: 8,
    padding: "6px 16px",
    fontSize: "0.85rem !important",
    "&:hover": {
      backgroundColor: "#1e40af",
    },
    "&.Mui-disabled": {
      backgroundColor: "#e2e8f0",
      color: "#94a3b8",
      border: "1px solid #cbd5e1",
    },
  },
  cancelBtn: {
    color: TEXT_TITLE,
    backgroundColor: "white",
    textTransform: "none",
    fontFamily: theme.typography.fontFamily,
    fontSize: "0.85rem !important",
    fontWeight: theme.typography.fontWeightSemiBold,
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    padding: "6px 16px",
    "&:hover": {
      backgroundColor: "#f8fafc",
      border: "1px solid #94a3b8",
    },
  },
}));

const initialState = {
  oemName: "",
  parentOemName: "",
  localPath: "",
};

const initialErrors = {
  oemName: "",
};

const AddOemDialog = ({ open, onClose, onAdded }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState(initialErrors);
  const [parentOems, setParentOems] = useState([]);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      dispatch(OAuthActions.getParentOems()).then(function (data) {
        if (data) {
          setParentOems(data);
        }
      });
    } else {
      setValues(initialState);
      setErrors(initialErrors);
      setFile(null);
      setParentOems([]);
      setSubmitting(false);
    }
  }, [open]);

  const validateField = (field, value) => {
    if (!value) return "";
    switch (field) {
      case "oemName":
        return NAME_REGEX.test(value)
          ? ""
          : "OEM Name must contain only alphabets, digits and underscore";
      default:
        return "";
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };

  const handleParentOem = (event, value) => {
    if (value == null) {
      setValues((prev) => ({ ...prev, parentOemName: "" }));
    } else {
      setValues((prev) => ({ ...prev, parentOemName: value.uid }));
    }
  };

  const onFileChange = (event) => {
    if (event.target.files[0]) {
      const selectedFile = event.target.files[0];
      const pattern = /image\/png/;

      if (!selectedFile.type.match(pattern)) {
        dispatch(
          setSnackbar(
            true,
            "error",
            "Please provide the company image, which should be in an image file format - png."
          )
        );
        return;
      }
      setFile(selectedFile);
      setValues((prev) => ({
        ...prev,
        localPath:
          selectedFile.name +
          " (" +
          (selectedFile.size / (1024 * 1024)).toFixed(2) +
          " MB )",
      }));
    }
  };

  const canSubmit =
    values.oemName.trim() !== "" &&
    errors.oemName === "" &&
    file !== null &&
    !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let formData = new FormData();
      formData.append("file", file);
      formData.append(
        "makeDto",
        JSON.stringify({ uid: values.parentOemName, name: values.oemName })
      );

      const success = await dispatch(OAuthActions.uploadOemDetails(formData));
      if (success && success.error === undefined) {
        dispatch(setSnackbar(true, "success", "OEM added successfully"));
        if (onAdded) onAdded();
        onClose();
      } else {
        dispatch(
          setAlertDialog(
            true,
            null,
            "error",
            "Document Upload",
            "Document upload was unsuccessful. Please check your network connectivity, and try again.\nDetails Of Failure: " +
            (success && success.message ? success.message : "Unknown error"),
            "OK",
            ""
          )
        );
      }
    } catch (err) {
      dispatch(
        setSnackbar(true, "error", "Unable to add OEM. Please try again.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.title}>
        <Typography variant="h6" component="h2" style={{ fontWeight: 700 }}>
          Add OEM
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Grid container spacing={2} className={classes.fieldGrid}>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemLeft}`}
          >
            <div className={classes.fieldLabel}>
              OEM Name <span className={classes.required}>*</span>
            </div>
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              value={values.oemName}
              onChange={handleChange("oemName")}
              error={errors.oemName.length !== 0}
              helperText={errors.oemName}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemRight}`}
          >
            <div className={classes.fieldLabel}>Parent OEM Name</div>
            <Autocomplete
              options={parentOems}
              getOptionLabel={(option) => option.name || ""}
              onChange={handleParentOem}
              ListboxProps={{
                style: {
                  maxHeight: 180,
                  overflow: "auto",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="filled"
                  hiddenLabel
                  className={classes.field}
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                  }}
                />
              )}
              PaperComponent={(props) => (
                <div
                  {...props}
                  className={classes.selectMenuPaper}
                  style={{
                    ...props.style,
                    overflow: "hidden",
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} className={classes.fieldGridItem}>
            <div className={classes.fieldLabel}>
              OEM Logo <span className={classes.required}>*</span>
            </div>
            <input
              accept="image/png"
              style={{ display: "none" }}
              id="oem-logo-file"
              type="file"
              onChange={onFileChange}
            />
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              value={values.localPath}
              className={classes.field}
              InputProps={{
                disableUnderline: true,
                readOnly: true,
              }}
            />
            <div style={{ marginTop: 8 }}>
              <label htmlFor="oem-logo-file">
                <Button
                  component="span"
                  variant="contained"
                  className={classes.primaryBtn}
                >
                  Browse File
                </Button>
              </label>
            </div>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          className={classes.cancelBtn}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          className={classes.primaryBtn}
          variant="contained"
          disableElevation
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={
            submitting ? (
              <CircularProgress size={16} style={{ color: "white" }} />
            ) : null
          }
        >
          {submitting ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOemDialog;
